export type ToolLandingContent = {
  heading: string;
  summary: string;
  sections?: Array<{
    heading: string;
    paragraphs: string[];
  }>;
  useCasesHeading?: string;
  useCases: string[];
  workflowHeading?: string;
  workflowSteps?: string[];
  faqs?: Array<{
    question: string;
    answer: string;
  }>;
};

export type ToolDataHandling = {
  label: string;
  summary: string;
  details: string[];
};

export const TOOL_LANDING_CONTENT: Record<string, ToolLandingContent> = {
  "campaign-url-architect": {
    heading: "Build campaign links without losing the naming system.",
    summary:
      "Campaign URL Architect turns a destination URL and campaign details into a clean tracked link, then keeps recent links in local history so recurring campaigns stay consistent.",
    useCases: [
      "Preparing paid, email, social, or partner campaign URLs before launch.",
      "Checking that UTM values are present, readable, and free of stray spaces.",
      "Reusing previous campaign structures without keeping a separate spreadsheet open.",
    ],
    workflowSteps: [
      "Paste the final landing page URL first so the campaign link starts from the right destination.",
      "Fill in source, medium, campaign, term, and content values using the team's naming pattern.",
      "Copy the finished URL and keep the generated history entry for the next campaign.",
    ],
    faqs: [
      {
        question: "Does Campaign URL Architect use GA4 campaign parameters?",
        answer:
          "Yes. The generated URL uses the standard utm_campaign field along with source, medium, term, and content values when those fields are filled in.",
      },
      {
        question: "Can I reuse old campaign naming patterns?",
        answer:
          "Recent links are stored locally so recurring channels, naming conventions, and campaign structures can be rebuilt without opening a separate tracker.",
      },
    ],
  },
  "case-converter": {
    heading: "A letter case converter for fast text cleanup.",
    summary:
      "This free case converter changes uppercase to lowercase, all caps to lowercase, title case, sentence case, alternating case, and code-friendly formats without retyping the source text.",
    sections: [
      {
        heading:
          "Convert uppercase, lowercase, title case, sentence case, and alternating case",
        paragraphs: [
          "Paste text once, then choose the letter case the destination needs. The converter handles visible copy formats such as lowercase, UPPERCASE, Title Case, Sentence case, Capitalized Case, Alternating case, and Inverse case.",
          "For filenames, URLs, variables, and structured labels, the same workspace can produce slug or kebab case, snake_case, camelCase, PascalCase, and CONSTANT_CASE output.",
        ],
      },
      {
        heading: "How to convert all caps to lowercase",
        paragraphs: [
          "Copy the all-caps text, paste it into the input field, and select lowercase. The converter uppercase-to-lowercase action preserves the words and punctuation while changing capital letters to lowercase.",
          "This also covers searches such as cap to lowercase or all caps to lowercase: there is no need to edit each letter by hand, even when the source is a long heading, spreadsheet column, transcript, or CMS export.",
        ],
      },
    ],
    useCasesHeading: "When to use a case converter",
    useCases: [
      "Changing all-caps notes, headings, labels, or exports into readable lowercase text before publishing.",
      "Turning rough copy into title case or sentence case for a document, design, email, or CMS field.",
      "Creating alternating or inverse case for informal creative text without editing characters individually.",
      "Normalizing labels into slug or kebab case, snake case, camel case, or constant case before development handoff.",
    ],
    workflowHeading: "How to use this case converter",
    workflowSteps: [
      "Paste the source text exactly as it came from the note, CMS, spreadsheet, transcript, or design file.",
      "Choose lowercase, uppercase, title case, sentence case, capitalized case, alternating case, or a URL-friendly format.",
      "Review the converted text, character count, and word count in the output panel.",
      "Copy the cleaned result, or clear the tool and repeat with the next paragraph, label list, or group of strings.",
    ],
    faqs: [
      {
        question: "Can I use this as an uppercase to lowercase converter?",
        answer:
          "Yes. Use it to convert uppercase text to lowercase: paste uppercase or all-caps text, choose lowercase, and copy the result without changing the original words or punctuation.",
      },
      {
        question: "What does a letter case converter change?",
        answer:
          "A letter case converter changes capitalization and, for code-friendly formats, word separators. It does not rewrite the meaning or grammar of the source text.",
      },
      {
        question: "Which case should I use for visible copy?",
        answer:
          "Sentence case and title case are common for visible copy. Lowercase and uppercase work for specific styles, while alternating case is usually best reserved for informal or creative text.",
      },
      {
        question: "Can it convert text for code and filenames?",
        answer:
          "Yes. The tool can create slug or kebab case, snake_case, camelCase, PascalCase, and CONSTANT_CASE strings for code-adjacent labels, URLs, and filenames.",
      },
    ],
  },
  "color-palette-generator": {
    heading: "Generate a color palette, test the scheme, and preview it in context.",
    summary:
      "This free color palette generator builds harmonious color schemes for branding, websites, interfaces, and creative projects. Lock or edit individual colors, compare WCAG contrast, preview mockups and gradients, recolor an image, then export the finished palette.",
    sections: [
      {
        heading: "Generate harmonious color schemes",
        paragraphs: [
          "Start with a generated palette, choose a color harmony, or edit a swatch by its HEX value. Lock the colors that should stay fixed, then generate again to explore new combinations around them.",
          "Harmony options help turn color-theory relationships into practical color schemes. The palette workspace also supports undo and redo, so a promising direction is easy to compare with the next variation.",
        ],
      },
      {
        heading: "Check color contrast before using the palette",
        paragraphs: [
          "Each swatch includes WCAG contrast information against light and dark text. Use those readings to identify which colors are better suited to backgrounds, text, accents, borders, or decorative elements.",
          "Contrast results are a useful preflight, but the final interface should still be tested at its real text sizes, weights, and component states.",
        ],
      },
      {
        heading: "Preview, recolor, and export palette colors",
        paragraphs: [
          "Open the visualizer to see the current palette in a mockup, build gradients from its colors, or apply the palette to an uploaded image for a quick recoloring study.",
          "Export the finished color palette as JSON, SVG, HTML, or PDF so the color values can move into design tokens, CSS, a presentation, or brand documentation.",
        ],
      },
    ],
    useCasesHeading: "When to use a color palette generator",
    useCases: [
      "Generating a brand color palette for a logo, identity system, or campaign.",
      "Exploring website and UI color schemes before creating design tokens or components.",
      "Checking whether palette colors have useful contrast against white or black text.",
      "Previewing a color scheme in gradients, interface mockups, and image recoloring studies.",
    ],
    workflowHeading: "How to generate a color palette",
    workflowSteps: [
      "Generate a starting palette or edit a swatch to the HEX color you already know.",
      "Lock the colors you want to keep, choose a harmony, and generate variations around them.",
      "Review WCAG contrast, mockup, gradient, and recoloring views before committing to the scheme.",
      "Export the palette as JSON, SVG, HTML, or PDF for design and development handoff.",
    ],
    faqs: [
      {
        question: "What does a color palette generator do?",
        answer:
          "A color palette generator creates groups of colors that can be explored together for branding, websites, interfaces, illustrations, presentations, and other design work.",
      },
      {
        question: "Can I build a color scheme around one HEX color?",
        answer:
          "Yes. Edit a swatch to the HEX value you want, lock that color, then generate new harmony-based variations while the locked swatch stays in the palette.",
      },
      {
        question: "Does the palette generator check WCAG contrast?",
        answer:
          "Yes. Each palette color includes contrast information against white and black text to help with early accessibility decisions.",
      },
      {
        question: "When should I use the mockup views?",
        answer:
          "Use mockup views before final export to see whether the palette still works in interface, marketing, and visual contexts instead of only as isolated swatches.",
      },
      {
        question: "Which color palette formats can I export?",
        answer:
          "The current export menu supports JSON, SVG, HTML, and PDF files for design tokens, CSS handoff, documentation, and presentation-ready references.",
      },
    ],
  },
  "color-picker": {
    heading: "Get a HEX code from any image.",
    summary:
      "This free image color picker samples HEX, RGB, and HSL values from an uploaded screenshot, logo, photo, or graphic so you can pick a color from an image and turn a visual reference into a usable design value.",
    sections: [
      {
        heading: "Find a HEX code from image pixels",
        paragraphs: [
          "Upload the image, move the picker over the target area, and use the magnified preview to select the exact pixel. The current HEX code appears beside its RGB and HSL equivalents for one-click copying.",
          "The same pixel sample lets you get RGB from an image and copy HSL when channel values or hue controls fit the next design task better than HEX.",
          "Nearby pixels may differ because of shadows, gradients, anti-aliasing, or image compression. Zooming in before copying helps avoid choosing an edge color by accident.",
        ],
      },
      {
        heading: "Move image colors into a design system",
        paragraphs: [
          "Use the sampled value in CSS, a design token, a brand palette, or a presentation theme. HEX is convenient for web work, RGB describes the screen color channels, and HSL makes lightness or saturation adjustments easier to reason about.",
        ],
      },
    ],
    useCasesHeading: "When to use an image color picker",
    useCases: [
      "Matching a HEX color from a screenshot, logo, product photo, or moodboard.",
      "Checking nearby pixels with a magnified view before copying the final value.",
      "Translating visual references into usable CSS or design-token color formats.",
      "Comparing a sampled brand color with an existing palette before design handoff.",
    ],
    workflowHeading: "How to get a HEX code from an image",
    workflowSteps: [
      "Upload the image or screenshot that contains the color you need to match.",
      "Use the magnified picker to sample the exact pixel or compare nearby options.",
      "Review the HEX, RGB, and HSL values generated for the selected color.",
      "Copy the preferred format into your CSS, design system, or palette notes.",
    ],
    faqs: [
      {
        question: "Can I get a HEX code from a screenshot?",
        answer:
          "Yes. Upload the screenshot, sample the target pixel, and copy the HEX value for design or front-end use.",
      },
      {
        question: "Why does one image area produce slightly different colors?",
        answer:
          "Anti-aliasing, shadows, compression, and gradients can change nearby pixels. The zoomed picker helps compare those pixels before copying the final value.",
      },
      {
        question: "What image formats can I upload?",
        answer:
          "The image picker accepts JPG, JPEG, PNG, and WebP files supported by the browser.",
      },
      {
        question: "Should I copy HEX, RGB, or HSL?",
        answer:
          "HEX is common in design files and CSS, RGB is useful when channel values matter, and HSL is convenient when you want to adjust hue, saturation, or lightness.",
      },
      {
        question: "Is my uploaded image private?",
        answer:
          "Yes. The image is read and sampled locally in your browser and is not uploaded to NoGatekeeping or sent to a third-party service.",
      },
    ],
  },
  dictate: {
    heading: "Capture spoken notes while the thought is still moving.",
    summary:
      "Dictate uses the browser's speech-recognition and speech-synthesis features for editable transcripts, playback, and session-based undo while drafting notes.",
    useCases: [
      "Drafting rough notes, outlines, messages, or reminders by voice.",
      "Reviewing dictated text, making edits, and listening back for flow.",
      "Using session-based undo and redo while shaping a rough spoken draft.",
    ],
    workflowSteps: [
      "Start a new dictation session when the note, outline, or message is ready to speak.",
      "Review the transcript, clean up wording, and use playback when the phrasing needs a second pass.",
      "Copy the finished transcript into another app before closing or refreshing the page.",
    ],
    faqs: [
      {
        question: "Is Dictate useful for rough drafts?",
        answer:
          "Yes. It is best for fast first-pass notes, outlines, reminders, and messages that can be edited before they become final copy.",
      },
      {
        question: "Does Dictate replace a full writing app?",
        answer:
          "No. It is a lightweight capture workspace for turning speech into editable text before moving the result into a document, task manager, or message.",
      },
    ],
  },
  "dns-checker": {
    heading: "Check DNS propagation from more than one resolver.",
    summary:
      "DNS Checker queries public resolvers in parallel so domain changes can be compared across record types, response values, and lookup timing.",
    useCases: [
      "Verifying A, AAAA, MX, TXT, CNAME, or NS records after a DNS change.",
      "Comparing resolver responses when a domain works in one place but not another.",
      "Spotting propagation gaps before debugging hosting, email, or verification issues.",
    ],
    workflowSteps: [
      "Enter the domain and choose the DNS record type that matches the launch or verification task.",
      "Compare resolver responses, values, timing, and empty results side by side.",
      "Use mismatches to decide whether to wait for propagation or investigate the hosting, email, or DNS provider setup.",
    ],
    faqs: [
      {
        question: "Which DNS records can I check?",
        answer:
          "DNS Checker is designed for common launch and verification records including A, AAAA, MX, TXT, CNAME, and NS records.",
      },
      {
        question: "Why do different resolvers show different answers?",
        answer:
          "Resolver caches can update at different times. Comparing them helps separate normal propagation delay from a real configuration issue.",
      },
    ],
  },
  "font-converter": {
    heading: "Turn font files into practical web and desktop formats.",
    summary:
      "Font Converter batches font uploads, processes supported formats, and produces ready-to-download outputs for front-end builds, design handoff, or asset cleanup.",
    useCases: [
      "Preparing font assets for web projects that need browser-friendly formats.",
      "Converting several font files at once instead of running one-off exports.",
      "Organizing delivered typography assets before adding them to a project.",
    ],
    workflowSteps: [
      "Upload the font files that need to be prepared for the target project.",
      "Choose the output format needed for the web build, desktop handoff, or asset archive.",
      "Download the converted files and keep the batch together for implementation or delivery.",
    ],
    faqs: [
      {
        question: "When should I convert font files?",
        answer:
          "Convert fonts when a project needs a browser-friendly format, a desktop-compatible handoff, or a cleaned-up set of typography assets.",
      },
      {
        question: "Can Font Converter handle batches?",
        answer:
          "Yes. It is built for batch uploads so several font files can move through the same conversion workflow together.",
      },
    ],
  },
  "heatmap-tracker": {
    heading: "Prototype heatmap ideas before adding analytics infrastructure.",
    summary:
      "Heatmap Tracker lets you place and export local click hotspots on a screenshot, making it useful for demos, design reviews, and early analytics planning.",
    useCases: [
      "Mocking up how click activity could look on a page or product screen.",
      "Annotating a screenshot with interaction hotspots during UX review.",
      "Exporting local heatmap point data for a prototype or proof of concept.",
    ],
    workflowSteps: [
      "Upload or prepare the screen that needs an interaction heatmap concept.",
      "Place hotspot points where clicks, taps, or attention should be represented.",
      "Export the heatmap view or point data for a review, prototype, or planning note.",
    ],
    faqs: [
      {
        question: "Is Heatmap Tracker a production analytics tool?",
        answer:
          "No. It is a local prototyping tool for mockups, demos, UX reviews, and early analytics planning before adding real tracking infrastructure.",
      },
      {
        question: "What should I use it with?",
        answer:
          "Use it with screenshots, product screens, landing page comps, or prototype states where a visual click pattern would clarify the discussion.",
      },
    ],
  },
  "image-compressor": {
    heading: "Compress an image to a target file size in KB.",
    summary:
      "This free image compressor can reduce image size automatically to 20KB, 50KB, 100KB, 200KB, 500KB, or a custom target. You can also control quality and dimensions directly, process a batch, and export optimized images without uploading them.",
    sections: [
      {
        heading: "Target an exact maximum file size",
        paragraphs: [
          "Choose Target file size and enter the maximum number of kilobytes. The compressor tests quality levels and reduces dimensions only when necessary to produce the largest practical result under that limit.",
          "Target-size mode is useful for application forms, profile photos, CMS limits, email attachments, and any upload field that rejects images above a fixed size.",
        ],
      },
      {
        heading: "Decrease picture size and image resolution",
        paragraphs: [
          "Lower the quality setting to reduce file weight, or enter maximum dimensions when the image resolution needs to decrease as well. Width and height stay proportional so the resized result does not stretch.",
          "A modest resolution decrease is often enough for oversized screenshots, product images, and photos that will be displayed much smaller than their original dimensions.",
        ],
      },
      {
        heading: "Condense a batch of images in one pass",
        paragraphs: [
          "Add several PNG, JPG, JPEG, or WebP files, apply one set of compression controls, and compare the original and compressed sizes. Download a single result or keep the batch together with ZIP export.",
          "Use the quality slider to balance a smaller file against visual detail. PNG compression remains lossless, while JPG and WebP can use the selected quality level for a stronger reduction.",
        ],
      },
    ],
    useCasesHeading: "When to use an image compressor",
    useCases: [
      "Compressing an image below a fixed 20KB, 50KB, 100KB, 200KB, or custom upload limit.",
      "Reducing large images before publishing them to a website, CMS, or product page.",
      "Decreasing picture size or resolution while preserving the original aspect ratio.",
      "Compressing a batch of screenshots, product images, or content assets together.",
      "Preparing lighter image attachments for email, documentation, or design handoff.",
    ],
    workflowHeading: "How to use this image compressor",
    workflowSteps: [
      "Drop in the images that are too heavy for the page, CMS, or handoff.",
      "Choose a target size in KB, or set quality plus maximum dimensions manually.",
      "Review compression results, resolution changes, and file-size savings before downloading.",
      "Export individual files or a ZIP when the optimized batch is ready.",
    ],
    faqs: [
      {
        question: "Can I compress an image to a specific size in KB?",
        answer:
          "Yes. Target file size mode automatically adjusts encoding quality and, when necessary, dimensions to get the output under your selected KB limit.",
      },
      {
        question: "Can I decrease image resolution with this compressor?",
        answer:
          "Yes. Set a max width and max height to resize large images down while keeping the aspect ratio intact.",
      },
      {
        question: "When should I compress images?",
        answer:
          "Compress images before publishing, uploading to a CMS, sending a handoff, or adding large screenshots to a page where file weight and picture size matter.",
      },
      {
        question: "Can I condense more than one image at once?",
        answer:
          "Yes. Image Compressor supports batch workflows and ZIP export so related assets can be condensed and downloaded together.",
      },
      {
        question: "Does image compression change the dimensions?",
        answer:
          "Only when maximum width or height is set below the original dimensions. Otherwise, the tool can reduce file size while keeping the original width and height.",
      },
      {
        question: "Are my images uploaded anywhere?",
        answer:
          "No. Compression runs locally in your browser, so your JPG, PNG, and WebP files are not uploaded to NoGatekeeping or a third-party service.",
      },
    ],
  },
  "image-converter": {
    heading: "Convert image formats without leaving the browser.",
    summary:
      "Image Converter changes images into JPG, PNG, WebP, or PDF outputs with batch controls, file previews, and downloadable results.",
    useCases: [
      "Preparing images for a platform that requires a specific file type.",
      "Converting several design, screenshot, or content assets in one pass.",
      "Creating PDFs from image files for sharing, archiving, or quick review.",
    ],
    workflowSteps: [
      "Add one JPG, JPEG, PNG, WebP, GIF, or AVIF image, or a batch of supported files that need a different output format.",
      "Pick the target format, then preview the conversion results before export.",
      "Download individual outputs or use batch export when several files are ready.",
    ],
    faqs: [
      {
        question: "Which image formats can I upload?",
        answer:
          "The current browser converter accepts JPG, JPEG, PNG, WebP, GIF, and AVIF images. It does not claim support for PSD, RAW, TIFF, HEIC, or PDF input files.",
      },
      {
        question: "Which image formats can I convert to?",
        answer:
          "Image Converter is built around common practical outputs including JPG, PNG, WebP, and PDF.",
      },
      {
        question: "Will an animated GIF stay animated?",
        answer:
          "No. The browser conversion path captures the decoded frame as a still image, so animation is not preserved in JPG, PNG, WebP, or PDF output.",
      },
      {
        question: "Why convert images in the browser?",
        answer:
          "Browser conversion is useful for quick platform requirements, content cleanup, and batch handoffs without opening a heavier design app.",
      },
    ],
  },
  "image-extractor": {
    heading: "Extract and download images from any public webpage URL.",
    summary:
      "Image Extractor scans HTML images, responsive srcset candidates, lazy-load attributes, CSS backgrounds, social metadata, and fallback source text. Filter by type or dimensions, copy source URLs, export TXT, or download selected assets as a ZIP.",
    sections: [
      {
        heading: "Find more than basic IMG tags",
        paragraphs: [
          "Modern pages often place the useful file in srcset, a lazy-loading attribute, a CSS background, or Open Graph metadata. The scanner checks each of those locations and favors higher-resolution URL variants when a page exposes several versions.",
          "Image validation and detected dimensions make it easier to distinguish usable photos and graphics from blocked requests, icons, and thumbnails.",
        ],
      },
      {
        heading: "Filter and export the useful assets",
        paragraphs: [
          "Search the discovered URLs, filter by file type or minimum width, and sort by image size. Select only the relevant results before copying the URLs, exporting a text inventory, or creating a ZIP.",
        ],
      },
    ],
    useCases: [
      "Reviewing page imagery during content, migration, or redesign work.",
      "Finding source image URLs without manually inspecting page markup.",
      "Collecting visible page assets for local analysis or cleanup.",
      "Exporting a list of image source URLs for migration or content inventory work.",
    ],
    workflowSteps: [
      "Enter the webpage URL that needs an image inventory.",
      "Filter discovered image URLs by keyword, type, width, or resolution.",
      "Copy source URLs, export a TXT inventory, or download selected images as a ZIP.",
    ],
    faqs: [
      {
        question: "Which webpage images can the extractor find?",
        answer:
          "It checks standard and lazy-loaded image tags, responsive srcset sources, inline and stylesheet background URLs, social metadata images, preload links, and common image URLs found in page source.",
      },
      {
        question: "When is Image Extractor useful?",
        answer:
          "It is useful during redesigns, migrations, content audits, and cleanup passes when image assets need to be found from a live page.",
      },
      {
        question: "Does Image Extractor replace source files?",
        answer:
          "No. It helps recover and inspect discoverable page assets when source files are missing or scattered.",
      },
    ],
  },
  "image-to-text": {
    heading: "Extract text from one image or a full batch.",
    summary:
      "This free image-to-text converter runs OCR locally in the browser to extract words from screenshots, documents, notes, labels, and graphics. Process one image for detailed review or add several and download combined TXT or a ZIP of individual text files.",
    sections: [
      {
        heading: "Upload or paste an image",
        paragraphs: [
          "Upload a PNG, JPG, JPEG, WebP, or GIF file, drag it into the drop zone, or paste a copied image while the uploader is focused. A clear, high-resolution source gives OCR the best chance of recognizing every line correctly.",
        ],
      },
      {
        heading:
          "Extract text from screenshots, documents, notes, and graphics",
        paragraphs: [
          "The image-to-text converter analyzes visible characters and turns image text into editable text. Use it to pull text from an image, screenshot, scanned page, handwritten or printed note, product label, slide, or graphic where the words cannot be selected directly.",
          "OCR confidence helps identify uncertain lines. Blurry, skewed, low-contrast, or highly stylized source images may need a quick manual correction after extraction.",
        ],
      },
      {
        heading: "Copy or download the result as plain text",
        paragraphs: [
          "Review the extracted words, correct any OCR mistakes, then copy or download the result as a TXT file. For a batch, download one combined text file or keep each result separate in a ZIP.",
        ],
      },
    ],
    useCasesHeading: "When to extract text from an image",
    useCases: [
      "Pulling text from screenshots, scanned notes, labels, photos, or image-based documents.",
      "Turning image text into editable text for notes, content cleanup, or data entry.",
      "Checking OCR confidence before reusing the extracted result.",
      "Recovering words from slides, social graphics, receipts, or reference images when source copy is unavailable.",
      "Running OCR across several screenshots or scanned pages in one batch.",
    ],
    workflowHeading: "How to use this image-to-text converter",
    workflowSteps: [
      "Upload, drag, or paste the screenshot, scan, label, note, or image-based document.",
      "Choose the document language so OCR uses the right recognition data.",
      "Run OCR and compare the image-to-text result with the source and confidence details.",
      "Correct any uncertain characters, then copy or download the plain-text result.",
    ],
    faqs: [
      {
        question: "Can this convert image text to text I can copy?",
        answer:
          "Yes. Upload an image with readable text, run OCR in the browser, then review, edit, and copy the extracted text.",
      },
      {
        question: "What kinds of images work best for OCR?",
        answer:
          "Clear screenshots, scans, labels, and high-contrast text images usually produce better results than blurry, skewed, or heavily stylized text.",
      },
      {
        question: "Why check OCR confidence?",
        answer:
          "Confidence helps flag areas that may need manual review before the extracted text is reused in content, documentation, or data entry.",
      },
      {
        question: "Which languages can the OCR tool recognize?",
        answer:
          "The language selector currently includes English, Spanish, French, German, Italian, Portuguese, Simplified Chinese, Japanese, and Russian recognition data.",
      },
      {
        question: "Does my image leave the browser?",
        answer:
          "The selected image is processed in the browser by the OCR library. The OCR script and language data are loaded from third-party hosting, but No Gatekeeping does not upload or store the source image.",
      },
    ],
  },
  "lorem-ipsum-generator": {
    heading: "Generate placeholder copy at the size you actually need.",
    summary:
      "Lorem Ipsum Generator creates filler text by paragraph, sentence, or word count so mockups and content slots can be tested quickly.",
    useCases: [
      "Filling design comps, wireframes, and prototypes with realistic text volume.",
      "Testing layout behavior for short blurbs, long paragraphs, or repeated sections.",
      "Copying placeholder text without opening a separate document or generator site.",
    ],
    workflowSteps: [
      "Choose whether the layout needs words, sentences, or paragraphs.",
      "Set the amount of placeholder copy needed for the content slot.",
      "Copy the generated text into the mockup, prototype, CMS field, or test layout.",
    ],
    faqs: [
      {
        question: "Why use generated placeholder text?",
        answer:
          "Placeholder text helps test layout rhythm, wrapping, spacing, and empty content states before final copy is ready.",
      },
      {
        question: "Can I control the amount of text?",
        answer:
          "Yes. Lorem Ipsum Generator can create text by word, sentence, or paragraph count so the output fits the exact slot.",
      },
    ],
  },
  "modular-wireframe-generator": {
    heading: "Assemble page structure before polishing the visual design.",
    summary:
      "Modular Wireframe Generator combines common website sections, variation controls, and export options for fast planning of page layouts.",
    useCases: [
      "Exploring landing page, pricing, feature, gallery, contact, or testimonial structures.",
      "Comparing section variations before committing to a full design pass.",
      "Exporting a rough layout for discussion, documentation, or next-step design work.",
    ],
    workflowSteps: [
      "Add the page sections that match the structure you need to explore.",
      "Cycle variations until the rough hierarchy, rhythm, and order make sense.",
      "Export the wireframe for feedback, documentation, or the next design pass.",
    ],
    faqs: [
      {
        question: "What kinds of pages can I wireframe?",
        answer:
          "The tool is useful for landing pages, pricing pages, feature sections, galleries, contact layouts, testimonials, and other common website structures.",
      },
      {
        question: "Is this meant for polished visual design?",
        answer:
          "No. It is for fast structure planning before visual polish, copy refinement, and detailed component design.",
      },
    ],
  },
  "scroll-animator-studio": {
    heading: "Design scroll-driven motion without hand-coding every keyframe.",
    summary:
      "Scroll Animator Studio combines a visual stage, percentage-based timeline, easing controls, starter templates, live preview, and multi-format code export for building section-scoped scroll animations.",
    sections: [
      {
        heading: "Build motion on a visual stage and timeline",
        paragraphs: [
          "Add layout containers, position and resize them directly on the stage, then place milestones at the scroll percentages where the motion should change. Each milestone can control position, dimensions, scale, rotation, opacity, stacking order, and corner radius while the containers remain available for designers or developers to fill with site content.",
          "Alignment guides, safe-area markers, timeline scrubbing, and cubic-bezier controls make it possible to refine the animation without repeatedly switching between source code and the browser.",
        ],
      },
      {
        heading: "Preview and export the finished scroll scene",
        paragraphs: [
          "Preview mode turns the current timeline into a scrollable full-screen scene so the animation can be checked at its intended pace before export.",
          "The export workspace produces a linked index.html, style.css, and script.js project, plus a React or Next.js component and a reusable container-slot snippet. Download the three-file project together as a ZIP or copy and download each file independently.",
        ],
      },
    ],
    useCasesHeading: "When to use Scroll Animator Studio",
    useCases: [
      "Prototyping hero reveals, expanding containers, layered stacks, and horizontal scroll scenes.",
      "Planning motion timing and easing before handing production code to a developer.",
      "Creating a standalone scroll-animation demo for review or experimentation.",
      "Generating reusable HTML, CSS, JavaScript, or React starting points for a website section.",
    ],
    workflowHeading: "How to build a scroll animation",
    workflowSteps: [
      "Start from an empty scene or load one of the included motion templates.",
      "Add and arrange containers, then scrub the timeline to the percentage where a new milestone should be placed.",
      "Adjust geometry, visibility, stacking, corner radius, and easing for each milestone.",
      "Choose the scroll distance, review the full-screen preview, and export the format needed for implementation.",
    ],
    faqs: [
      {
        question: "Does Scroll Animator Studio upload my animation project?",
        answer:
          "No. Elements, milestones, previews, and code generation stay in the current browser session. The tool does not create an account or upload a project to a server.",
      },
      {
        question: "Which formats can I export?",
        answer:
          "You can export a linked index.html, style.css, and script.js project as a ZIP, download those files individually, or copy a React or Next.js component and reusable HTML container markup.",
      },
      {
        question: "How do I add site content to an animated container?",
        answer:
          "Each exported solid-color container includes an empty, named HTML slot. Add your markup or framework component inside that container after the layout motion is ready; its exported color remains as the base background unless you override it.",
      },
    ],
  },
  "proportion-scaler": {
    heading: "Resize dimensions while keeping the ratio intact.",
    summary:
      "Proportion Scaler calculates proportional width and height values from an original size, making aspect-ratio math quick and copyable.",
    useCases: [
      "Resizing images, embeds, canvases, videos, or design frames without distortion.",
      "Finding a matching dimension when only one side is known.",
      "Checking common ratios before exporting or implementing fixed-format assets.",
    ],
    workflowSteps: [
      "Enter the original width and height to define the starting ratio.",
      "Change one target dimension and let the matching side update automatically.",
      "Copy the proportional dimensions into the export dialog, CSS, embed, or design frame.",
    ],
    faqs: [
      {
        question: "What is Proportion Scaler best for?",
        answer:
          "It is best for resizing images, videos, canvases, embeds, thumbnails, and design frames while preserving the original aspect ratio.",
      },
      {
        question: "Why preserve aspect ratio?",
        answer:
          "Preserving aspect ratio prevents stretching and keeps fixed-format assets looking correct across exports, embeds, and responsive layouts.",
      },
    ],
  },
  "redirect-analyzer": {
    heading: "Map where a URL really redirects.",
    summary:
      "Redirect Analyzer is a redirect mapping tool that follows a URL through status codes, hops, final destinations, and protocol changes so redirect behavior is easier to debug.",
    useCases: [
      "Checking whether a campaign, affiliate, or migrated URL lands in the right place.",
      "Finding extra redirect hops with a redirect chain checker before they slow down a page or obscure the final URL.",
      "Confirming HTTP-to-HTTPS upgrades and status codes during SEO cleanup.",
    ],
    workflowSteps: [
      "Paste the URL that needs to be checked before launch, migration, or campaign use.",
      "Review each redirect hop, status code, protocol change, and final destination.",
      "Use the chain to remove unnecessary hops or confirm that the final URL is correct.",
    ],
    faqs: [
      {
        question: "Why audit redirect chains?",
        answer:
          "Redirect chains can slow pages, hide final destinations, break tracking, or preserve old migration mistakes. Mapping the hops makes those issues easier to fix.",
      },
      {
        question: "What should a clean redirect look like?",
        answer:
          "A clean redirect usually reaches the intended HTTPS destination with as few hops as possible and with a status code that matches the purpose of the redirect.",
      },
    ],
  },
  "sitemap-robots-explorer": {
    heading: "Map crawl hints from robots.txt and sitemap files.",
    summary:
      "Sitemap Robots Explorer reads a site entry point, discovers sitemap references, scans URLs, and groups discovered paths into useful categories.",
    useCases: [
      "Getting a fast view of a website's public URL structure.",
      "Checking whether robots.txt exposes expected sitemap locations.",
      "Categorizing discovered URLs before a content audit, migration, or technical SEO review.",
    ],
    workflowSteps: [
      "Enter the site URL or robots.txt entry point for the crawl hint review.",
      "Review sitemap references, discovered URLs, and grouped path categories.",
      "Use the output to plan a content audit, migration check, or technical SEO pass.",
    ],
    faqs: [
      {
        question: "What does Sitemap Robots Explorer look for?",
        answer:
          "It looks for robots.txt rules, sitemap references, discovered sitemap URLs, and path groups that help explain the public crawl surface of a site.",
      },
      {
        question: "When should I run a sitemap and robots check?",
        answer:
          "Run it before audits, launches, migrations, and cleanup work so crawl hints and public URL coverage are visible early.",
      },
    ],
  },
  vectorvault: {
    heading: "Manage SVG icons from a lightweight local library.",
    summary:
      "Vector Vault provides search, preview, editing, upload, and export flows for SVG icons without turning icon cleanup into a larger asset-management task.",
    useCases: [
      "Finding and copying reusable SVG icons during interface design or development.",
      "Editing icon details before export or handoff.",
      "Keeping a practical local icon library for repeated project work.",
    ],
    workflowSteps: [
      "Search or upload icons that belong in the local SVG library.",
      "Preview, edit, and organize the icon before copying or exporting it.",
      "Reuse the cleaned SVG in interface work, design handoff, or project documentation.",
    ],
    faqs: [
      {
        question: "What is Vector Vault for?",
        answer:
          "Vector Vault is for keeping a lightweight local SVG library searchable, editable, and ready for repeated design and development work.",
      },
      {
        question: "Can I edit icons before export?",
        answer:
          "Yes. The workflow supports previewing, editing, uploading, and exporting SVG icons so cleanup can happen before handoff.",
      },
    ],
  },
};

export const TOOL_DATA_HANDLING: Record<string, ToolDataHandling> = {
  "campaign-url-architect": {
    label: "Browser-only input with local history",
    summary:
      "Campaign fields are combined in this browser. Generated-link history is saved in localStorage on this device so it can be reused later.",
    details: [
      "No account is required and campaign values are not sent to a No Gatekeeping API.",
      "Clearing this site's browser storage removes the saved campaign history.",
    ],
  },
  "case-converter": {
    label: "Browser-only processing",
    summary:
      "Pasted text is transformed in page memory. The converter does not upload the source text or save it after the page is closed.",
    details: [
      "Copying a result uses the browser clipboard only after you press Copy.",
      "Refreshing the page clears the current text workspace.",
    ],
  },
  "color-palette-generator": {
    label: "Browser processing with local favorites",
    summary:
      "Palette generation, contrast work, gradients, and recoloring run in the browser. Favorite palettes are stored in localStorage on this device.",
    details: [
      "Uploaded recoloring references remain in the active browser session.",
      "Clearing this site's browser storage removes saved favorite palettes.",
    ],
  },
  "color-picker": {
    label: "Browser-only image sampling",
    summary:
      "The selected image is decoded and sampled locally in the browser. It is not uploaded to No Gatekeeping or an image-processing service.",
    details: [
      "Pixel values remain in the current page session.",
      "Clipboard access happens only when you choose to copy a color value.",
    ],
  },
  dictate: {
    label: "Browser speech service with session-only editing",
    summary:
      "Dictate uses the Web Speech API supplied by your browser. Depending on the browser and operating system, speech recognition may be processed by the browser vendor's service.",
    details: [
      "No Gatekeeping does not save transcripts to an account or database.",
      "Undo history lasts only for the current page session, so copy important text before leaving.",
    ],
  },
  "dns-checker": {
    label: "Server-assisted network lookup",
    summary:
      "The domain, record type, and selected resolver are sent to the No Gatekeeping lookup endpoint so the server can query public DNS resolvers.",
    details: [
      "Do not enter secrets; DNS records and domain names are public network data.",
      "The page displays the resolver responses returned for the requested lookup.",
    ],
  },
  "font-converter": {
    label: "Browser-only file conversion",
    summary:
      "Font parsing and conversion run in the browser with the loaded conversion libraries. Uploaded font files are not sent to a No Gatekeeping API.",
    details: [
      "Converted files are created as temporary browser objects for download.",
      "Only upload fonts you have permission to use and convert.",
    ],
  },
  "heatmap-tracker": {
    label: "Local prototype data",
    summary:
      "Screenshots, seeded demo points, and manually placed hotspots stay in the current browser session. This tool does not collect real visitor analytics.",
    details: [
      "Export happens only when you choose to download the prototype JSON.",
      "The tool is for design review and planning, not production tracking.",
    ],
  },
  "image-compressor": {
    label: "Browser-only image processing",
    summary:
      "JPG, PNG, and WebP compression and resizing run locally in the browser. Source images are not uploaded to No Gatekeeping or a third-party processor.",
    details: [
      "Results exist as temporary browser files until you download or remove them.",
      "Batch ZIP creation also happens in the browser.",
    ],
  },
  "image-converter": {
    label: "Browser-only image conversion",
    summary:
      "Supported image files are decoded with browser APIs and converted locally to JPG, PNG, WebP, or a single-image PDF.",
    details: [
      "Files are not uploaded to a No Gatekeeping conversion endpoint.",
      "Animated GIF input is converted from the frame the browser decodes; animation is not preserved in the output.",
    ],
  },
  "image-extractor": {
    label: "URL lookup with proxy fallback",
    summary:
      "The entered page URL is requested from the browser. If cross-origin rules block that request, the tool tries AllOrigins and corsproxy.io to retrieve public page markup.",
    details: [
      "Image downloads may contact the source host or the same proxy fallback.",
      "Use the tool only for public pages and assets you are allowed to retrieve.",
    ],
  },
  "image-to-text": {
    label: "Local OCR with externally loaded library files",
    summary:
      "OCR runs in the browser and the selected image is not uploaded to No Gatekeeping. Tesseract code and language resources are loaded from its distribution network when needed.",
    details: [
      "Extracted text remains in the active page until you copy or clear it.",
      "OCR output can contain mistakes and should be reviewed before reuse.",
    ],
  },
  "lorem-ipsum-generator": {
    label: "Browser-only generation",
    summary:
      "Placeholder text and byte counts are generated in page memory. No prompt or generated text is sent to a No Gatekeeping service.",
    details: [
      "Copying text uses the browser clipboard after an explicit action.",
      "Generated text is not saved after the page is closed.",
    ],
  },
  "modular-wireframe-generator": {
    label: "Browser-only layout work",
    summary:
      "Wireframe variations and exports are assembled in the browser. No project account or server-side document is created.",
    details: [
      "Exported HTML may reference Tailwind's CDN and Google Fonts when you open it.",
      "Downloaded layouts remain under your control on your device.",
    ],
  },
  "scroll-animator-studio": {
    label: "Browser-only animation design",
    summary:
      "Elements, milestones, easing values, previews, and generated code remain in the current browser session. No animation project is uploaded to No Gatekeeping.",
    details: [
      "Exported containers remain blank so you can add site markup or framework components after designing the motion.",
      "Clipboard and file-download actions happen only after you request them.",
    ],
  },
  "proportion-scaler": {
    label: "Browser-only calculation",
    summary:
      "Dimensions and aspect-ratio calculations run in page memory. Entered values are not uploaded or stored after the session.",
    details: [
      "Copy actions use the browser clipboard only when requested.",
      "The calculator does not inspect or upload an image file.",
    ],
  },
  "redirect-analyzer": {
    label: "Server-assisted URL request",
    summary:
      "The submitted URL is sent to the No Gatekeeping redirect endpoint, which requests the public URL and follows redirect responses up to the tool's limit.",
    details: [
      "The destination site can see the server request and the NoGatekeepingBot user agent.",
      "Do not submit private, authenticated, or internal-network URLs.",
    ],
  },
  "sitemap-robots-explorer": {
    label: "Public URL lookup through third-party proxies",
    summary:
      "The entered site URL and discovered sitemap URLs are sent to AllOrigins or corsproxy.io so public robots.txt and sitemap files can be read across origins.",
    details: [
      "Only use the scanner with public URLs that are appropriate to share with those proxy services.",
      "The tool reports crawl hints and listed URLs; it does not reveal private or unlinked pages.",
    ],
  },
  vectorvault: {
    label: "Local browser library",
    summary:
      "The built-in SVG library and icons you add are managed in localStorage on this device. No icon account or cloud library is created.",
    details: [
      "Clearing this site's browser storage removes icons you added locally.",
      "Clipboard and download actions happen only after you request them.",
    ],
  },
};
